import React from "react";
import * as RadixDialog from "@radix-ui/react-dialog";
import { ThemeProvider, createTheme, useTheme } from "@mui/material/styles";
import { cn } from "../../lib/cn";

/**
 * claudesign Dialog — thin Radix wrapper at MUI Dialog/Modal parity.
 * Chrome mirrors docs/redesign/guest-system.css `.dialog` / `.dlg-dim`.
 * Supersedes ad-hoc MUI <Dialog>/<Modal> usage across components/Modal/*.
 *
 * Compound API — callers own the inner padded body + actions:
 *   <Dialog.Root open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
 *     <Dialog.Content className="max-w-[480px]">
 *       <Dialog.Title>Delete track?</Dialog.Title>
 *       ...body / actions...
 *     </Dialog.Content>
 *   </Dialog.Root>
 *
 * Radix gives focus-trap, scroll-lock, Esc + overlay-click close, and aria wiring.
 * Radix requires a <Dialog.Title> (or aria-label) for a11y; pass
 * aria-describedby={undefined} on Content when there is no <Dialog.Description>.
 *
 * Content also hosts MUI's portals (see MuiPortalHost) so MUI dropdowns opened
 * inside a dialog stay clickable.
 */

/**
 * Radix `modal` dialogs set `document.body.style.pointerEvents = "none"` and
 * re-enable pointers only on Dialog.Content. MUI portals its popups (the
 * Autocomplete popper, Select/Menu popover) to `document.body` by default —
 * i.e. OUTSIDE Content — so a dropdown opened, showed its options, and then
 * swallowed every click on them.
 *
 * Furthermore, Dialog.Content is transformed (`-translate-x-1/2 -translate-y-1/2`)
 * and `overflow-hidden`. Portalling directly into Content creates a transformed
 * containing block that displaces MUI's viewport-calculated coordinates and
 * clips dropdowns extending outside the card.
 *
 * Instead, we portal MUI popups into an untransformed sibling host container
 * under RadixDialog.Portal (`fixed inset-0 z-[1402] pointer-events-none`).
 * Dropdowns render at true viewport coordinates without clipping, while
 * onPointerDownOutside/onInteractOutside/onFocusOutside guards prevent Radix from
 * dismissing the dialog when the user clicks dropdown options.
 *
 * Merging onto the outer theme preserves the global MuiPopper z-index and style
 * overrides from theme.tsx.
 */
const MuiPortalHost: React.FC<{
  container: HTMLElement | null;
  children: React.ReactNode;
}> = ({ container, children }) => {
  const outer = useTheme();
  const theme = React.useMemo(
    () =>
      createTheme(outer, {
        components: {
          MuiPopper: { defaultProps: { container: container ?? undefined } },
          MuiPopover: { defaultProps: { container: container ?? undefined } },
          MuiModal: {
            defaultProps: {
              container: container ?? undefined,
              disableEnforceFocus: true,
              disableAutoFocus: true,
            },
          },
        },
      }),
    [outer, container],
  );
  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};

export const Root = RadixDialog.Root;
export const Trigger = RadixDialog.Trigger;
export const Close = RadixDialog.Close;

export const Content = React.forwardRef<
  React.ElementRef<typeof RadixDialog.Content>,
  React.ComponentPropsWithoutRef<typeof RadixDialog.Content> & { sheet?: boolean }
>(({ className, children, sheet, onPointerDownOutside, onInteractOutside, onFocusOutside, ...props }, ref) => {
  // Held in state, not a ref: MuiPortalHost must re-render once the portal host exists.
  const [portalHostEl, setPortalHostEl] = React.useState<HTMLDivElement | null>(null);

  // Radix's hideOthers() sets aria-hidden="true" on all siblings of Dialog.Content.
  // aria-live="off" on the portal host keeps hideOthers from suppressing it, but we
  // also maintain an observer in case aria-hidden attributes are ever applied.
  React.useEffect(() => {
    if (!portalHostEl) return;
    const cleanAriaHidden = () => {
      if (portalHostEl.getAttribute("aria-hidden") === "true") {
        portalHostEl.removeAttribute("aria-hidden");
      }
      if (portalHostEl.getAttribute("data-aria-hidden") === "true") {
        portalHostEl.removeAttribute("data-aria-hidden");
      }
    };
    cleanAriaHidden();
    const observer = new MutationObserver(cleanAriaHidden);
    observer.observe(portalHostEl, { attributes: true, attributeFilter: ["aria-hidden", "data-aria-hidden"] });
    return () => observer.disconnect();
  }, [portalHostEl]);

  const handlePointerDownOutside = React.useCallback(
    (event: CustomEvent<{ originalEvent: PointerEvent }>) => {
      const originalEvent = event.detail?.originalEvent;
      const target = (originalEvent?.target || (event as any).target) as Node | null;
      if (target && portalHostEl?.contains(target)) {
        event.preventDefault();
      }
      onPointerDownOutside?.(event);
    },
    [portalHostEl, onPointerDownOutside],
  );

  const handleInteractOutside = React.useCallback(
    (event: CustomEvent) => {
      const originalEvent = (event as any).detail?.originalEvent;
      const target = (originalEvent?.target || (event as any).target) as Node | null;
      if (target && portalHostEl?.contains(target)) {
        event.preventDefault();
      }
      onInteractOutside?.(event);
    },
    [portalHostEl, onInteractOutside],
  );

  const handleFocusOutside = React.useCallback(
    (event: CustomEvent) => {
      const target = (event as any).target as Node | null;
      if (target && portalHostEl?.contains(target)) {
        event.preventDefault();
      }
      onFocusOutside?.(event);
    },
    [portalHostEl, onFocusOutside],
  );

  return (
    <RadixDialog.Portal>
      <RadixDialog.Overlay className="fixed inset-0 z-[1400] bg-overlay" />
      <div
        ref={setPortalHostEl}
        data-mui-portal-host=""
        aria-live="off"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: 0,
          height: 0,
          zIndex: 1402,
          pointerEvents: "auto",
        }}
      />
      <RadixDialog.Content
        ref={ref}
        onPointerDownOutside={handlePointerDownOutside}
        onInteractOutside={handleInteractOutside}
        onFocusOutside={handleFocusOutside}
        className={cn(
          sheet
            ? [
                // ≥sm: the default centered card. Only the positioning that the phone
                // chrome below changes is sm:-prefixed; sizing/radius/overflow stay
                // UNPREFIXED so pre-existing caller overrides (e.g. max-w-[560px],
                // overflow-y-auto) keep winning at ≥sm via cn/twMerge ordering.
                "fixed z-[1401] sm:left-1/2 sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2",
                "w-[calc(100%-32px)] max-w-[480px]",
                "border border-border bg-surface rounded-lg overflow-hidden",
                // phones: bottom sheet. max-sm: rules live in a later @media block, so
                // with important:true they beat the unprefixed defaults (and caller
                // overrides) below sm by stylesheet source order.
                "max-sm:inset-x-0 max-sm:bottom-0 max-sm:top-auto",
                "max-sm:w-full max-sm:max-w-none max-sm:rounded-t-[20px] max-sm:rounded-b-none",
                "max-sm:max-h-[85dvh] max-sm:overflow-y-auto",
                "max-sm:pb-[max(16px,env(safe-area-inset-bottom))]",
                "max-sm:animate-[sheet-up_.28s_ease-out]",
              ]
            : [
                "fixed left-1/2 top-1/2 z-[1401] -translate-x-1/2 -translate-y-1/2",
                "w-[calc(100%-32px)] max-w-[480px]",
                "border border-border bg-surface rounded-lg overflow-hidden",
              ],
          "shadow-[0_50px_130px_-45px_rgba(0,0,0,.9)] focus:outline-none",
          className,
        )}
        {...props}
      >
        {sheet && (
          <div aria-hidden className="sm:hidden sticky top-0 flex justify-center pt-2.5">
            <div className="h-1 w-9 rounded-full bg-fill-3" />
          </div>
        )}
        <MuiPortalHost container={portalHostEl}>{children}</MuiPortalHost>
      </RadixDialog.Content>
    </RadixDialog.Portal>
  );
});
Content.displayName = "DialogContent";

export const Title = React.forwardRef<
  React.ElementRef<typeof RadixDialog.Title>,
  React.ComponentPropsWithoutRef<typeof RadixDialog.Title>
>(({ className, ...props }, ref) => (
  <RadixDialog.Title
    ref={ref}
    className={cn("text-[23px] font-bold tracking-[-0.015em] text-white", className)}
    {...props}
  />
));
Title.displayName = "DialogTitle";

export const Description = React.forwardRef<
  React.ElementRef<typeof RadixDialog.Description>,
  React.ComponentPropsWithoutRef<typeof RadixDialog.Description>
>(({ className, ...props }, ref) => (
  <RadixDialog.Description
    ref={ref}
    className={cn("text-[14.5px] leading-[1.55] text-muted", className)}
    {...props}
  />
));
Description.displayName = "DialogDescription";

/** Namespaced object export, mirroring how callers use Radix compound components. */
export const Dialog = { Root, Trigger, Close, Content, Title, Description };
export default Dialog;
